import React, { createContext, useEffect, useMemo, useState } from "react";
import productData from "../Datas/productData";
import { addUniqueLicense, calculateCartTotal, licenseKey, makeOrderNumber } from "../utils/commerce";
import { loadState, saveState } from "../utils/storage";

export const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [store, setStore] = useState(loadState);

  useEffect(() => {
    saveState(store);
  }, [store]);

  const addCart = (productId, version = "full") => {
    setStore((current) => {
      const cart = addUniqueLicense(current.cart, productId, version);
      return cart === current.cart ? current : { ...current, cart };
    });
  };

  const removeCart = (productId, version = "full") => {
    setStore((current) => ({
      ...current,
      cart: current.cart.filter((item) => !(item.id === productId && item.version === version)),
    }));
  };

  const updateQuantity = () => 1;
  const clearCart = () => setStore((current) => ({ ...current, cart: [] }));

  const addWishlist = (productId) =>
    setStore((current) => current.wishlist.includes(productId)
      ? current
      : { ...current, wishlist: [...current.wishlist, productId] });

  const removeWishlist = (productId) =>
    setStore((current) => ({ ...current, wishlist: current.wishlist.filter((id) => id !== productId) }));

  const toggleWishlist = (productId) =>
    setStore((current) => ({
      ...current,
      wishlist: current.wishlist.includes(productId)
        ? current.wishlist.filter((id) => id !== productId)
        : [...current.wishlist, productId],
    }));

  const updateProgress = (productId, result) => {
    setStore((current) => {
      const previous = current.progress[productId] || { plays: 0, bestScore: 0 };
      return {
        ...current,
        progress: {
          ...current.progress,
          [productId]: {
            plays: previous.plays + 1,
            bestScore: Math.max(previous.bestScore || 0, Number(result.score) || 0),
            lastResult: result.status,
            updatedAt: new Date().toISOString(),
          },
        },
      };
    });
  };

  const isUnlocked = (productId, version = "full") => store.unlocks.includes(licenseKey(productId, version));

  const createOrder = (email, orderNumber = makeOrderNumber()) => {
    if (!store.cart.length) return null;
    const items = store.cart.map((item) => ({ ...item }));
    const order = {
      id: orderNumber,
      email,
      items,
      total: calculateCartTotal(items, productData),
      currency: "USD",
      createdAt: new Date().toISOString(),
      status: "paid",
    };
    const newUnlocks = items.map((item) => licenseKey(item.id, item.version));
    setStore((current) => ({
      ...current,
      cart: [],
      orders: [order, ...current.orders].slice(0, 10),
      unlocks: [...new Set([...current.unlocks, ...newUnlocks])],
      currentOrderId: order.id,
    }));
    return order;
  };

  const clearLocalData = () => setStore({
    version: 3,
    cart: [],
    wishlist: [],
    progress: {},
    orders: [],
    unlocks: [],
    currentOrderId: null,
  });

  const cartTotal = useMemo(() => calculateCartTotal(store.cart, productData), [store.cart]);
  const currentOrder = store.orders.find((order) => order.id === store.currentOrderId) || null;

  const value = {
    cartItems: store.cart,
    wishlist: store.wishlist,
    progress: store.progress,
    orders: store.orders,
    unlocks: store.unlocks,
    currentOrder,
    cartTotal,
    addCart,
    addToCart: addCart,
    removeCart,
    removeFromCart: removeCart,
    updateQuantity,
    clearCart,
    addWishlist,
    removeWishlist,
    toggleWishlist,
    updateProgress,
    isUnlocked,
    createOrder,
    clearLocalData,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
